import { GridColDef } from '@mui/x-data-grid';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Alert,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { atom, useSetAtom } from 'jotai';

import {
  useAllChoiceGroup,
  useDeleteChoiceGroup,
  usePatchChoiceGroup,
} from '../../Query/ChoiceGroupQuery'

import AddIcon from '@mui/icons-material/Add';

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { COLORS } from '../../../theme/themeConstants';
import EditStoreOwnerModal from '../../stateless/EditModel';
import AddChoiceGroup from './AddChoiceGroup';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import ActionButtons from '../../stateless/Common/ActionButtons';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import { useGroupChoiceParams } from './useGroupChoiceParams';
import { useDebounce } from '../../../utils/useDounce';

export interface ChoiceGroupDataType {
  storeType: any;
  choiceGroupNameEnglish: string;
  choiceGroupNameArabic: string;
  minimumLimit: number | null;
  maximumLimit: number | null;
  choiceDetails: any;
  choiceGroupOption: any;
  merchantDetails: any;
  id: any;
  storeId?: string;
}

export const choiceGroupAtom = atom<ChoiceGroupDataType | null>(null);

interface ManageChoicesGroupTableProps {
  storeId?: string;
}

const ManageChoiceGroupTable: React.FC<ManageChoicesGroupTableProps> = () => {
  const { showMessage } = useSnackbar();
  const { t, i18n } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');
  const [, forceUpdate] = useState({});

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);

    const { params, dispatch } = useGroupChoiceParams();
    const isReady = Boolean(params && Object.keys(params).length > 0);
  
    const memoizedParams = useMemo(() => {
      return isReady ? params : {};
    }, [isReady, params]);
    const { data: ChoiceGroupData, isLoading, refetch} = useAllChoiceGroup(memoizedParams)

  const setChoiceGroupAtom = useSetAtom(choiceGroupAtom);


  const { mutate: patchChoiceGroup } = usePatchChoiceGroup();

  const { mutate: deleteChoiceGroup } = useDeleteChoiceGroup();

  const filteredRows = React.useMemo(() => {
    if (!ChoiceGroupData?.data) return [];
    const storeOwners = ChoiceGroupData?.data || []

    return storeOwners.map((row, index) => ({
      ...row,
       id: row.choiceGroupId,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.storeName?? 'N/A',
        merchantType: row.storeType ?? 'N/A',
        ChoiceGroup: row.choiceGroupNameEnglish,
        minimumLimit: row.minimumLimit,
        maximumLimit: row.maximumLimit,
        choiceDetails: row.choiceDetails.map((detail) => detail.choiceId),
    }));
  }, [params.search, ChoiceGroupData, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  // const handleFilterChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
  //   dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  // };

  const handleBlockClick = useCallback(async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice group to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchChoiceGroup({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  }, [selectedRows, patchChoiceGroup, showMessage]);

  const handleUnBlockClick = useCallback(async () => {
    if (selectedRows.length === 0) {
      showMessage(
        'Please select at least one choice group to unblock',
        'error'
      );
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchChoiceGroup({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  }, [selectedRows, patchChoiceGroup, showMessage]);

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice group to delete', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteChoiceGroup(selectedRows);
    }
  }, [selectedRows]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteChoiceGroup([id]);
    },
    [selectedRows, deleteChoiceGroup]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = useCallback((row: any) => {
    setChoiceGroupAtom(row);
    setOpenEditDialog(true);
  }, [setChoiceGroupAtom]);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditDialog(false);
    setChoiceGroupAtom(null);
  }, [setChoiceGroupAtom]);

  const handleBlockClickButton = useCallback(async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this Choice group?'
    );
    if (!confirmed) return;

    await patchChoiceGroup({
      id: [id],
      data: { status: 'blocked' },
    });
  }, [patchChoiceGroup]);

  const handleUnBlockClickButton = useCallback(async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this Choice group?'
    );
    if (!confirmed) return;

    await patchChoiceGroup({
      id: [id],
      data: { status: 'unblocked' },
    });
  }, [patchChoiceGroup]);

  const totalItems = params.search
    ? filteredRows.length
    : ChoiceGroupData?.pagination?.totalItems || 0

  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = ChoiceGroupData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.choiceGroupId), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, ChoiceGroupData]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    return (
      <>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              handleEditClick(row);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('choiceGroup.editAction')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              const id = typeof row.id === 'string' ? row.id : '';
              if (id) {
                handleDeleteButton(id);
              }
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>{t('choiceGroup.deleteAction')}</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  };

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'sno',
        headerName: t('choiceGroup.columns.sno'),
      flex: 0.3,
    },
    {
      field: 'storeName',
      headerName: t('choiceGroup.columns.storeName'),
      flex: 2,
    },
    {
      field: 'storeType',
      headerName: t('choiceGroup.columns.storeType'),
      flex: 2,
    },
    {
      field: 'ChoiceGroup',
      headerName: t('choiceGroup.columns.choiceGroup'),
      flex: 1,
    },
    {
      field: 'minimumLimit',
      headerName: t('choiceGroup.columns.min'),
      flex: 1,
    },
    {
      field: 'maximumLimit',
      headerName: t('choiceGroup.columns.max'),
      flex: 1,
    },
    {
      field: 'status',
      headerName: t('choiceGroup.columns.status'),
      flex: 0.5,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id;
        const validId = typeof id === 'string' ? id : '';

        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={
              () =>
                isBlocked
                  ? handleUnBlockClickButton(validId)
                  : handleBlockClickButton(validId)
            }
          >
            {isBlocked ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
      {
      field: 'actions',
      headerName: t('choiceGroup.columns.actions'),
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
    // Delete column removed in favor of Actions menu
  ], [t, handleEditClick, handleUnBlockClickButton, handleBlockClickButton, handleDeleteButton]);

  // Format the manageStoreData for the DataGrid

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder={t('choiceGroup.searchPlaceholder')}
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <ActionButtons
              onBlock={handleBlockClick}
              onUnblock={handleUnBlockClick}
              onDelete={handleDeleteClick}
            showBlock={showBlock}
            showUnblock={showUnblock}
            showDelete={selectedRows.length > 0}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSubAdmin}
              size="small"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: COLORS.primary.main,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: COLORS.primary.main,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              {t('choiceGroup.add')}
            </Button>
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title={t('choiceGroup.add')}
        >
          <AddChoiceGroup onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={
            modalContent === 'add' ? t('choiceGroup.add') : t('choiceGroup.edit')
          }
        >
          <AddChoiceGroup onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
        />
      </Box>
    </>
  );
};

export default ManageChoiceGroupTable;

